import React from 'react';
import { Column, Row } from 'Core/components/Layout';
import { PrimaryButton, TextButton } from 'Core/components/Button';
import { useTranslation } from 'react-i18next';

import { Alert } from 'Core/components/Alert';
import { useGetNextStep } from 'Modules/financing/hooks/useGetNextStep';

import Spinner from 'Core/components/Spinner';
import FinancingSummary from './FinancingSummary';
import SendLink from 'Modules/financing/FinalizeOffer/SendLink';
import { postLogMessage, features, actions } from 'Core/utils/logger';
import { getUrlParamIgnoreCase } from 'Core/utils/utils';
import FinancingModal from 'Core/components/FinancingModal/FinancingModal';
import Loader from 'Core/components/Loader';

export default function FinalizerOffer({
  application,
  enableSignContract,
  watch,
  setRefetchInterval,
  routes,
}) {
  const [state, setState] = React.useState({
    refetchInterval: false,
    financingWindow: null,
    isSignContractLoading: false,
    isError: false,
    isModalOpen: false,
  });

  const { t } = useTranslation();
  const [isTeleaudiology, setIsTeleaudiology] = React.useState(false);
  const blockFinalizeOfferForFinalQuote =
    getUrlParamIgnoreCase('blockFinalizeOfferForFinalQuote') === 'true';
  const { data: nextStepApp } = useGetNextStep(state.refetchInterval);
  const finalApp = nextStepApp || application || {};
  const {
    external_reference_id = '',
    payload: {
      links: { cart: redirection_link },
      application_id = '',
      payment_id = '',
    },
  } = finalApp;

  React.useEffect(() => {
    const isSamePageFlow = getUrlParamIgnoreCase('samePageFlow') === 'true';
    setIsTeleaudiology(!!isSamePageFlow);
  }, []);

  const openFinancingWindow = (e) => {
    postLogMessage({
      feature: features.financingSignContract,
      action: actions.signContractButtonClicked,
      status: 'success',
      payment_id,
      external_reference_id,
      application_id,
    });

    e.preventDefault();
    if (redirection_link) {
      if (isTeleaudiology) {
        const financingWindow = window.open(redirection_link, '_self');
        setState({
          ...state,
          refetchInterval: 5000,
          financingWindow,
          isSignContractLoading: true,
        });
      } else {
        // Original behavior - open in new tab
        const financingWindow = window.open(redirection_link, '_blank');
        setState({
          ...state,
          refetchInterval: 5000,
          financingWindow,
          isSignContractLoading: true,
        });
      }
    }
  };

  const closeModal = () => {
    setState({
      ...state,
      isModalOpen: false,
    });
  };

  return (
    <>
      <div className="w-full m-auto">
        <div className="">
          <Alert type="shade" data-testid="notification-financing-intro">
            <div className="text-sm w-full">{t('synchrony.financingApproved.intro')}</div>
          </Alert>
          <FinancingSummary
            watch={watch}
            setRefetchInterval={setRefetchInterval}
            routes={routes}
            application={application}
          ></FinancingSummary>

          {state.isSignContractLoading && isTeleaudiology && (
            <Loader type="overlay" message="Redirecting to Contract page..." />
          )}
          <Row className="px-2 mt-4 items-center justify-center">
            {state.isSignContractLoading ? (
              <Row className="flex justify-center items-center w-full mx-0">
                <Column className="">
                  <hr></hr>
                </Column>
                <Column className="!flex-col-12 text-center mb-2">
                  <Spinner small className="!w-6 !h-6 !text-primary" />
                  <span className="pl-4 text-lg font-semibold">
                    {t('financing.nextgen.signContract.loadingText')}
                  </span>
                </Column>
                <Column className="!flex-col-12">
                  <TextButton
                    block
                    data-testid="set-offer"
                    onClick={openFinancingWindow}
                    disabled={!enableSignContract}
                  >
                    <span className="text-sm">
                      {isTeleaudiology
                        ? t('financing.openLinkAgain')
                        : t('financing.openLinkAgain')}
                    </span>
                  </TextButton>
                </Column>
                <Column className="lg:!flex-col-6 px-0">
                  <SendLink className="m-auto"></SendLink>
                </Column>
              </Row>
            ) : (
              <Row className="flex justify-center items-center w-full px-4">
                <Column className="!flex-col-1 lg:!flex-col-3 px-0"></Column>
                <Column className="lg:!flex-col-6 px-0">
                  <PrimaryButton
                    block
                    data-testid="set-offer"
                    onClick={openFinancingWindow}
                    disabled={!enableSignContract || blockFinalizeOfferForFinalQuote}
                  >
                    {t('common.signContract')}
                  </PrimaryButton>
                </Column>
                <Column className="!flex-col-3 text-right mb-2"></Column>
                <Column className="lg:!flex-col-6 px-0">
                  <SendLink className="m-auto"></SendLink>
                </Column>
              </Row>
            )}
          </Row>
        </div>
      </div>

      <FinancingModal isOpen={state.isModalOpen} onClose={closeModal} url={redirection_link} />
    </>
  );
}
